import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HeaderRenderer, HeaderRendererParams } from 'app/shared/components/smart-grid/core/interfaces/header-renderer.interface';

/**
 * Global action types
 */
export type GlobalActionType = 'synchronize' | 'move' | 'unify' | 'release';

/**
 * Global Actions Cell Renderer Component
 *
 * Renders action buttons/menu in the header row for bulk operations
 * Shows different actions based on document type:
 * - DT003: Menu with Synchronize, Move, Unify, Release
 * - Others: Direct synchronize button when available
 *
 * Based on: src/app/modules/components/monitor/table-load-list/table-load-list.component.html (lines 334-363)
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   colId: 'globalActions',
 *   headerName: '',
 *   cellRenderer: GlobalActionsCellRendererComponent,
 *   cellRendererParams: {
 *     documentType: 'DT003',
 *     selectedCount: () => this.selection.selected.length,
 *     canJoin: true,
 *     canRelease: true,
 *     isGlobalSynchronizeAvailable: () => this.isGlobalSynchronizeAvailable,
 *     onAction: (action: string) => this.handleGlobalAction(action)
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-global-actions-cell-renderer',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  template: `
    <!-- Menu for DT003 documents ONLY -->
    <button
      *ngIf="selectedCount > 0 && documentType === 'DT003'"
      mat-icon-button
      [matMenuTriggerFor]="actionMenu"
      matTooltip="Acciones">
      <mat-icon>more_vert</mat-icon>
    </button>

    <mat-menu #actionMenu="matMenu">
      <button mat-menu-item (click)="onAction('synchronize')" matTooltip="Sincronizar">
        <mat-icon svgIcon="heroicons_outline:arrow-path"></mat-icon>
        <span>Sincronizar</span>
      </button>
      <button mat-menu-item (click)="onAction('move')">
        <mat-icon svgIcon="heroicons_outline:pencil-square"></mat-icon>
        <span>Mover</span>
      </button>
      <button
        *ngIf="canJoin"
        mat-menu-item
        (click)="onAction('unify')"
        [disabled]="isNoOrderSelectionJoin">
        <mat-icon svgIcon="heroicons_outline:squares-plus"></mat-icon>
        <span>Unificar</span>
      </button>
      <button
        *ngIf="canRelease"
        mat-menu-item
        (click)="onAction('release')"
        [disabled]="isNoOrderSelectionRelease">
        <mat-icon svgIcon="heroicons_outline:lock-open"></mat-icon>
        <span>Liberar</span>
      </button>
    </mat-menu>

    <!-- Direct synchronize button for non-DT003 documents -->
    <button
      *ngIf="documentType !== 'DT003' && isGlobalSynchronizeAvailable"
      mat-icon-button
      color="primary"
      matTooltip="Sincronizar"
      (click)="onAction('synchronize')">
      <mat-icon svgIcon="heroicons_outline:arrow-path"></mat-icon>
    </button>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }
  `],
})
export class GlobalActionsCellRendererComponent implements HeaderRenderer<any>, OnInit {
  /**
   * Parameters from the grid containing configuration and callbacks
   */
  @Input() params!: HeaderRendererParams<any>;

  /**
   * Event emitted when an action is clicked
   * Parent component should handle these actions
   */
  @Output() actionClick = new EventEmitter<{ action: GlobalActionType }>();

  ngOnInit(): void {
    // Initialization logic if needed
  }


  /**
   * Document type from headerRendererParams
   * Can be a function or a direct value
   */
  get documentType(): string {
    const docType = this.params?.colDef?.headerRendererParams?.params?.documentType;
    if (typeof docType === 'function') {
      return docType() || '';
    }
    return docType || '';
  }

  /**
   * Number of selected rows (from callback in headerRendererParams)
   */
  get selectedCount(): number {
    const getSelectedCount = this.params?.colDef?.headerRendererParams?.params?.selectedCount;
    if (getSelectedCount && typeof getSelectedCount === 'function') {
      return getSelectedCount();
    }
    return 0;
  }

  /**
   * Can join permission (from headerRendererParams)
   * Supports both function (reactive) and direct value (backward compatibility)
   */
  get canJoin(): boolean {
    const getter = this.params?.colDef?.headerRendererParams?.params?.canJoin;
    if (typeof getter === 'function') {
      return getter();
    }
    return getter ?? false;
  }

  /**
   * Can release permission (from headerRendererParams)
   */
  get canRelease(): boolean {
    return this.params?.colDef?.headerRendererParams?.params?.canRelease ?? false;
  }

  /**
   * Is global synchronize available (from callback in headerRendererParams)
   */
  get isGlobalSynchronizeAvailable(): boolean {
    const getIsAvailable = this.params?.colDef?.headerRendererParams?.params?.isGlobalSynchronizeAvailable;
    if (getIsAvailable && typeof getIsAvailable === 'function') {
      return getIsAvailable();
    }
    return false;
  }

  /**
   * Check if no orders are selected for join action
   * Gets value from headerRendererParams callback
   */
  get isNoOrderSelectionJoin(): boolean {
    const getIsNoSelection = this.params?.colDef?.headerRendererParams?.params?.isNoOrderSelectionJoin;
    if (getIsNoSelection && typeof getIsNoSelection === 'function') {
      return getIsNoSelection();
    }
    // Fallback: disable if no items selected
    return this.selectedCount === 0;
  }

  /**
   * Check if no orders are selected for release action
   * Gets value from headerRendererParams callback
   */
  get isNoOrderSelectionRelease(): boolean {
    const getIsNoSelection = this.params?.colDef?.headerRendererParams?.params?.isNoOrderSelectionRelease;
    if (getIsNoSelection && typeof getIsNoSelection === 'function') {
      return getIsNoSelection();
    }
    // Fallback: disable if no items selected
    return this.selectedCount === 0;
  }

  /**
   * Handle action click
   * Calls the callback function from headerRendererParams if available
   */
  onAction(action: GlobalActionType): void {
    // Call the callback from params.params (passed from parent component)
    const onAction = this.params?.colDef?.headerRendererParams?.params?.onAction;
    if (onAction && typeof onAction === 'function') {
      onAction(action);
    } else {
      // Fallback to emitting event (for backward compatibility)
      this.actionClick.emit({ action });
    }
  }
}